import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { compose } from 'redux';
import { v4 as uuidv4 } from 'uuid';

import { tagsTypes } from 'app-components/flux/tags';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { tracker } from 'app-components/utils/core';
import Dump from 'components/Dump';
import QButton from 'components/QButton';
import QDialogs from 'components/QDialogs';
import QTag, { variants } from 'components/QTag';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';
import { List, isImmutable } from 'immutable';
import { withStyles } from 'tss-react/mui';
import DeleteTagDialog from './DeleteTagDialog';

const styles = ({ palette, spacing }) => ({
  sectionHeadline: {
    marginBottom: spacing(2),
  },
  headlineIcon: {
    maxHeight: spacing(4),
    maxWidth: spacing(4),
    width: 'auto',
    height: 'auto',
    marginRight: spacing(1),
    marginBottom: 4,
    color: palette.common.white,
  },
  iconSpacing: {
    marginRight: spacing(1),
  },
  tagsContainer: {
    width: '100%',
    padding: spacing(3),
    marginBottom: spacing(3),
    overflow: 'hidden',
    position: 'relative',
  },
  chip: {
    margin: spacing(1 / 2),
  },
  noData: {
    display: 'block',
    width: '100%',
    textAlign: 'center',
    padding: `${spacing(4)} 0`,
  },
  txnStatus: {
    color: palette.text.secondary,
    marginTop: spacing(1),
  },
});

function makeLocalTagData(tags) {
  return !tags ? List() : tags.toList();
}

const ID_NEW = '0';
const TAG_NEW = '';

export class TagsSettings extends React.PureComponent {
  constructor(props) {
    super(props);

    props.getItems();

    this.state = {
      data: this.doSort(makeLocalTagData(props.data)),
      editing: null,
      editingTag: null,
      showDeleteTagDialog: false,
      tag: null,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data !== this.props.data) {
      const newTagIndex = nextProps.data.findKey((x) => x.get('createdAt') === null);
      if (newTagIndex) {
        nextProps.getItems();
      }

      if (this.state.editingTag) {
        if (this.state.editingTag?.id === ID_NEW) {
          if (nextProps.data.find((x) => x.name === this.state.editingTag?.name?.trim?.()) === undefined) {
            const data = this.doSort(makeLocalTagData(nextProps.data)).unshift(this.state.editingTag);
            this.setState({ data });
          }
        } else {
          const data = this.doSort(makeLocalTagData(nextProps.data).unshift(this.state.editingTag));
          this.setState({ data });
        }
      } else {
        this.setState({ data: this.doSort(makeLocalTagData(nextProps.data)) });
      }
    }
  }

  doSort(data) {
    if (data.size === 0) {
      return data;
    }

    // Sort list, if found a new Tag, move it to the beginning
    const sortData = data.sort((a, b) => {
      if (isImmutable(a) && isImmutable(b)) {
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      }

      return 0;
    });

    return sortData;
  }

  checkForDuplicates(id, name, checkAll = false) {
    let myData;
    if (checkAll) {
      myData = this.state?.data;
    } else {
      myData = this.state?.data.filterNot((x) => x.get('id') === id); // ignore current tag in duplicates
    }
    const groupData = myData?.groupBy((x) => x.get('name')).mapKeys((x) => x.toLowerCase());
    return Boolean(groupData?.get(name.toLowerCase()));
  }

  handleRequestCreate = () => {
    const newIndex = this.state.data.findIndex((x) => x.id === ID_NEW);

    if (newIndex < 0) {
      //  createdAt time will be replaced with 'real' time upon refresh, it is set here so the sort order is not broken
      const newItem = tagsTypes.mkTag({ id: ID_NEW, name: TAG_NEW, createdAt: DateTime.local().toISO() });
      const newData = this.state.data.unshift(newItem);
      this.setState({ data: newData, editing: ID_NEW, editingTag: newItem });
    }
  };

  handleVariantChange = (id, tagVariant = variants.NORMAL) => {
    let newEditing;

    if (tagVariant === variants.NORMAL) {
      newEditing = null;
    }
    if (tagVariant === variants.EDIT) {
      newEditing = id;
    }

    this.setState({ editing: newEditing });
  };

  handleSave = (id, name, callback) => {
    if (this.validateTag(name)) {
      const dupTag = this.checkForDuplicates(id, name);

      if (dupTag) {
        this.props.dialogAlert(`${name} already exists.`, 'You need to use a unique name', () => undefined, ['Got It']);
      } else {
        let item = this.state.data.find((x) => x.id === id);

        if (item === undefined) {
          item = this.state.editingTag;
        }
        const toSave = item.set('name', name);

        if (id === ID_NEW) {
          tracker.track(tracker.events.tagCreate);
          const newId = uuidv4().toUpperCase();
          this.props.createItem(toSave.merge({ id: undefined, clientId: newId }));
          this.removeNewItem();
        } else {
          tracker.track(tracker.events.tagEdit);
          this.setState({ editingTag: null }, () => this.props.updateItem(toSave));
        }

        if (callback) {
          callback(dupTag);
        }
      }
    }
  };

  setEditingTag = (editingTag) => {
    this.setState({ editingTag });
  };

  validateTag(name) {
    if (name === '') {
      this.props.dialogAlert(
        'Unable to save tag',
        'You must specify a name for this tag',
        () => {
          this.handleRequestCreate();
        },
        ['Got It'],
      );
      return false;
    }
    return true;
  }

  handleDelete = (id) => {
    const tag = this.state.data.find((x) => x.id === id);
    this.setState({ ...this.state, tag, showDeleteTagDialog: true });
  };

  removeNewItem = () => {
    const { data } = this.state;
    const newIndex = data.findIndex((x) => x.id === ID_NEW);

    if (newIndex <= 0) {
      const newData = data.delete(newIndex);
      this.setState({
        data: newData,
        editingTag: null,
      });
    }
  };

  showSaveChangesDialog = (saveCallback, cancelCallback, tagValue) => {
    if (!this.checkForDuplicates(0, tagValue, true)) {
      this.props.dialogAlert(
        'Unsaved tag',
        'Do you want to save the changes?',
        (retObj) => {
          if (retObj.btnPressed === 'Cancel' || retObj.btnPressed === 'Escape') {
            if (cancelCallback) cancelCallback();
          }
          if (retObj.btnPressed === 'Save') {
            if (saveCallback) saveCallback();
          }
        },
        ['Cancel', 'Save'],
      );
    } else {
      if (cancelCallback) cancelCallback();
      this.props.dialogAlert(`${tagValue} already exists.`, 'You need to use a unique name', () => undefined, [
        'Got It',
      ]);
    }
  };

  renderTagItems() {
    const { classes } = this.props;
    const { data } = this.state;

    return data.map((tag, index) => {
      const { editing } = this.state;
      let tagVariant = variants.NORMAL;

      if (tag.id === editing) {
        tagVariant = tag.id === ID_NEW ? variants.NEW : variants.EDIT;
      }

      return (
        <Box position='relative' key={tag.id || ID_NEW}>
          <Dump obj={tag} />
          <QTag
            readOnly={tag.type === 'GOAL'}
            classname={classes.chip}
            key={tag.id || ID_NEW}
            id={tag.id}
            index={index}
            tagValue={tag.name}
            tagVariant={tagVariant}
            changeUiState={this.handleVariantChange}
            handleSave={this.handleSave}
            handleDelete={this.handleDelete}
            removeNewItem={this.removeNewItem}
            showSaveChangesDialog={this.showSaveChangesDialog}
            setEditingTag={this.setEditingTag}
          />
        </Box>
      );
    });
  }

  render() {
    const { classes, isLoading } = this.props;
    const { data } = this.state;

    return (
      <>
        <SettingsHeader
          title='Tags'
          primaryItem={
            <QButton id='tags-add-new-button' variant='add' onClick={this.handleRequestCreate}>
              Tags
            </QButton>
          }
        />
        <Paper variant={'outlined'} className={classes.tagsContainer}>
          <Grid container>
            {data.size
              ? this.renderTagItems()
              : !isLoading && (
                  <Typography className={classes.noData} component='p' variant='bodyMedium'>
                    No tags available. Please click the button to add a new tag
                  </Typography>
                )}
          </Grid>
        </Paper>
        {this.state.showDeleteTagDialog && (
          <DeleteTagDialog
            tag={this.state.tag}
            onClose={() => this.setState({ ...this.state, showDeleteTagDialog: false })}
            deleteItem={(props) => {
              this.setState({ ...this.state, showDeleteTagDialog: false });
              this.props.deleteItem(props);
            }}
          />
        )}
      </>
    );
  }
}

TagsSettings.defaultProps = {
  isLoading: true,
};

TagsSettings.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.object,
  dialogAlert: PropTypes.func,
  isLoading: PropTypes.bool,
  getItems: PropTypes.func,
  createItem: PropTypes.func,
  updateItem: PropTypes.func,
  deleteItem: PropTypes.func,
};

export default compose(QDialogs())(withStyles(TagsSettings, styles));
